import { useState, useEffect } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { Link } from "react-router-dom";
import { ticketAPI, adminAPI, categoryAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  Clock,
  Search,
  ChevronRight,
  Tag,
  Calendar,
  AlertCircle,
  Filter,
  X,
  Loader2,
} from "lucide-react";
import { motion } from "framer-motion";
import MultiSelect, { MultiSelectOption } from "../components/MultiSelect";

export default function TicketHistory() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState<any[]>([]);
  const [categories, setCategories] = useState<MultiSelectOption[]>([]);
  const [assignees, setAssignees] = useState<MultiSelectOption[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadTicketHistory();
    loadFilterOptions();
  }, []);

  const loadTicketHistory = async () => {
    try {
      setLoading(true);
      const response = await ticketAPI.list();

      if (response.success || Array.isArray(response)) {
        let ticketsList = Array.isArray(response)
          ? response
          : response.data?.data ||
            response.data?.items ||
            response.data?.tickets ||
            [];

        if (!Array.isArray(ticketsList)) {
          ticketsList = [];
        }

        setTickets(ticketsList);
      }
    } catch (error) {
      toast.error("Failed to load ticket history");
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const categoriesResponse = await categoryAPI.list();
      if (categoriesResponse.success && categoriesResponse.data) {
        const categoryList = Array.isArray(categoriesResponse.data)
          ? categoriesResponse.data
          : categoriesResponse.data.items || [];

        const categoryOptions = categoryList.map((cat: any) => ({
          value: cat.name,
          label: cat.name,
          color: "bg-blue-500/10 text-blue-400",
        }));

        setCategories(categoryOptions);
      }

      if (user?.role === "admin") {
        const techResponse = await adminAPI.technicianStats();
        if (techResponse.success && techResponse.data) {
          const techList = Array.isArray(techResponse.data)
            ? techResponse.data
            : [];

          const techOptions = techList.map((tech: any) => {
            const techData = tech.technician || tech;
            const techId = techData.id || tech.id;
            const techName =
              techData.fullName || techData.name || techData.email || "Unknown";

            return {
              value: techId,
              label: techName,
              color: "bg-purple-500/10 text-purple-400",
            };
          });

          setAssignees(techOptions);
        }
      }
    } catch (error) {
      toast.error("Failed to load filter options");
    }
  };

  const statusColors = {
    open: "bg-red-500/10 text-red-400 border-red-500/30",
    "in-progress": "bg-blue-500/10 text-blue-400 border-blue-500/30",
    pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
    resolved: "bg-green-500/10 text-green-400 border-green-500/30",
    closed: "bg-gray-500/10 text-gray-400 border-gray-500/30",
  };

  const priorityColors = {
    critical: "text-red-500",
    high: "text-orange-500",
    medium: "text-yellow-500",
    low: "text-blue-500",
  };

  const typeColors = {
    incident: "bg-red-500/10 text-red-400",
    request: "bg-blue-500/10 text-blue-400",
    complaint: "bg-purple-500/10 text-purple-400",
  };

  const statusOptions: MultiSelectOption[] = [
    {
      value: "open",
      label: "Open",
      color: "bg-red-500/10 text-red-400 border-red-500/30",
      badge: true,
    },
    {
      value: "in-progress",
      label: "In Progress",
      color: "bg-blue-500/10 text-blue-400 border-blue-500/30",
      badge: true,
    },
    {
      value: "pending",
      label: "Pending",
      color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
      badge: true,
    },
    {
      value: "resolved",
      label: "Resolved",
      color: "bg-green-500/10 text-green-400 border-green-500/30",
      badge: true,
    },
    {
      value: "closed",
      label: "Closed",
      color: "bg-gray-500/10 text-gray-400 border-gray-500/30",
      badge: true,
    },
  ];

  const priorityOptions: MultiSelectOption[] = [
    {
      value: "critical",
      label: "Critical",
      color: "bg-red-500/10 text-red-400",
      badge: true,
    },
    {
      value: "high",
      label: "High",
      color: "bg-orange-500/10 text-orange-400",
      badge: true,
    },
    {
      value: "medium",
      label: "Medium",
      color: "bg-yellow-500/10 text-yellow-400",
      badge: true,
    },
    {
      value: "low",
      label: "Low",
      color: "bg-blue-500/10 text-blue-400",
      badge: true,
    },
  ];

  const filteredTickets = tickets.filter((ticket) => {
    const title = ticket.title || "";
    const ticketId = ticket.ticketNumber || ticket.id || "";
    const status = (ticket.status || "").toLowerCase().replace(/_/g, "-");
    const priority = (ticket.priority || "").toLowerCase();
    const category = ticket.category?.name || "";
    const assigneeId = ticket.assignedTo?.id || ticket.assignedId || "";

    const matchesSearch =
      (title || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (ticketId || "").toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      selectedStatuses.length === 0 || selectedStatuses.includes(status);

    const matchesPriority =
      selectedPriorities.length === 0 || selectedPriorities.includes(priority);

    const matchesCategory =
      selectedCategories.length === 0 || selectedCategories.includes(category);

    const matchesAssignee =
      selectedAssignees.length === 0 || selectedAssignees.includes(assigneeId);

    return (
      matchesSearch &&
      matchesStatus &&
      matchesPriority &&
      matchesCategory &&
      matchesAssignee
    );
  });

  const stats = {
    total: filteredTickets.length,
    open: filteredTickets.filter(
      (t) => (t.status || "").toLowerCase() === "open",
    ).length,
    inProgress: filteredTickets.filter(
      (t) =>
        (t.status || "").toLowerCase().replace(/_/g, "-") === "in-progress",
    ).length,
    resolved: filteredTickets.filter(
      (t) => (t.status || "").toLowerCase() === "resolved",
    ).length,
    closed: filteredTickets.filter(
      (t) => (t.status || "").toLowerCase() === "closed",
    ).length,
  };

  const hasActiveFilters =
    selectedStatuses.length > 0 ||
    selectedPriorities.length > 0 ||
    selectedCategories.length > 0 ||
    selectedAssignees.length > 0 ||
    searchQuery.trim() !== "";

  const clearAllFilters = () => {
    setSelectedStatuses([]);
    setSelectedPriorities([]);
    setSelectedCategories([]);
    setSelectedAssignees([]);
    setSearchQuery("");
  };

  return (
    <DashboardLayout>
      <div className="min-h-full p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2 font-serif">
            My Ticket History
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            View all your submitted tickets and their current status
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          {[
            { label: "Total", value: stats.total, color: "text-primary" },
            { label: "Open", value: stats.open, color: "text-red-400" },
            {
              label: "In Progress",
              value: stats.inProgress,
              color: "text-blue-400",
            },
            {
              label: "Resolved",
              value: stats.resolved,
              color: "text-green-400",
            },
            { label: "Closed", value: stats.closed, color: "text-gray-400" },
          ].map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="bg-card border border-border/50 rounded-xl p-4 shadow-lg"
            >
              <p className="text-xs text-muted-foreground mb-1">{stat.label}</p>
              <p className={`text-2xl font-bold ${stat.color} font-mono`}>
                {stat.value}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card border border-border/50 rounded-xl p-4 sm:p-6 shadow-lg space-y-4"
        >
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="search"
                placeholder="Search tickets by ID or title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm"
              />
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`
                flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm cursor-pointer
                transition-all whitespace-nowrap
                ${
                  showFilters || hasActiveFilters
                    ? "bg-primary text-white"
                    : "bg-muted/50 text-foreground hover:bg-muted"
                }
              `}
            >
              <Filter className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="px-1.5 py-0.5 bg-white/20 rounded-full text-xs">
                  {[
                    selectedStatuses.length,
                    selectedPriorities.length,
                    selectedCategories.length,
                    selectedAssignees.length,
                  ].reduce((a, b) => a + b, 0)}
                </span>
              )}
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm bg-destructive/10 text-destructive hover:bg-destructive/20 transition-all whitespace-nowrap cursor-pointer"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>

          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className={`grid grid-cols-1 sm:grid-cols-2 ${
                user?.role === "admin" ? "lg:grid-cols-4" : "lg:grid-cols-3"
              } gap-4 pt-4 border-t border-border/50`}
            >
              <MultiSelect
                label="Status"
                options={statusOptions}
                value={selectedStatuses}
                onChange={setSelectedStatuses}
                placeholder="Select statuses..."
                maxDisplay={2}
              />

              <MultiSelect
                label="Priority"
                options={priorityOptions}
                value={selectedPriorities}
                onChange={setSelectedPriorities}
                placeholder="Select priorities..."
                maxDisplay={2}
              />

              <MultiSelect
                label="Category"
                options={categories}
                value={selectedCategories}
                onChange={setSelectedCategories}
                placeholder="Select categories..."
                maxDisplay={2}
              />

              {user?.role === "admin" && (
                <MultiSelect
                  label="Assignee"
                  options={assignees}
                  value={selectedAssignees}
                  onChange={setSelectedAssignees}
                  placeholder="Select assignees..."
                  maxDisplay={2}
                />
              )}
            </motion.div>
          )}

          {hasActiveFilters && (
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/50">
              <span className="text-xs text-muted-foreground font-medium">
                Active Filters:
              </span>
              {selectedStatuses.map((status) => (
                <span
                  key={status}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/10 text-red-400 rounded text-xs font-medium"
                >
                  Status: {status}
                  <button
                    onClick={() =>
                      setSelectedStatuses(
                        selectedStatuses.filter((s) => s !== status),
                      )
                    }
                    className="hover:bg-black/10 rounded-full p-0.5 cursor-pointer"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {selectedPriorities.map((priority) => (
                <span
                  key={priority}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-orange-500/10 text-orange-400 rounded text-xs font-medium"
                >
                  Priority: {priority}
                  <button
                    onClick={() =>
                      setSelectedPriorities(
                        selectedPriorities.filter((p) => p !== priority),
                      )
                    }
                    className="hover:bg-black/10 rounded-full p-0.5 cursor-pointer"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {selectedCategories.map((category) => (
                <span
                  key={category}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/10 text-blue-400 rounded text-xs font-medium"
                >
                  Category: {category}
                  <button
                    onClick={() =>
                      setSelectedCategories(
                        selectedCategories.filter((c) => c !== category),
                      )
                    }
                    className="hover:bg-black/10 rounded-full p-0.5 cursor-pointer"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {selectedAssignees.map((assignee) => (
                <span
                  key={assignee}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/10 text-purple-400 rounded text-xs font-medium"
                >
                  Assignee: {assignee}
                  <button
                    onClick={() =>
                      setSelectedAssignees(
                        selectedAssignees.filter((a) => a !== assignee),
                      )
                    }
                    className="hover:bg-black/10 rounded-full p-0.5 cursor-pointer"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-card border border-border/50 rounded-xl shadow-lg overflow-hidden"
        >
          <div className="divide-y divide-border/50">
            {loading ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="p-8 text-center">
                <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No tickets found</p>
              </div>
            ) : (
              filteredTickets.map((ticket) => (
                <Link
                  key={ticket.id}
                  to={`/dashboard/ticket/${ticket.id}`}
                  className="block p-4 sm:p-6 hover:bg-muted/30 transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`mt-1 w-1 h-16 rounded-full flex-shrink-0 ${
                        ticket.priority === "CRITICAL"
                          ? "bg-red-500"
                          : ticket.priority === "HIGH"
                            ? "bg-orange-500"
                            : ticket.priority === "MEDIUM"
                              ? "bg-yellow-500"
                              : "bg-blue-500"
                      }`}
                    />

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="text-sm font-mono font-semibold text-primary">
                          {ticket.ticketNumber || ticket.id}
                        </span>
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded border ${
                            statusColors[
                              ticket.status?.toLowerCase() as keyof typeof statusColors
                            ]
                          }`}
                        >
                          {ticket.status?.replace("_", " ")}
                        </span>
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded ${
                            typeColors[
                              ticket.type?.toLowerCase() as keyof typeof typeColors
                            ]
                          }`}
                        >
                          {ticket.type}
                        </span>
                        <span
                          className={`text-xs font-semibold ${
                            priorityColors[
                              ticket.priority?.toLowerCase() as keyof typeof priorityColors
                            ]
                          }`}
                        >
                          {ticket.priority}
                        </span>
                      </div>

                      <h3 className="text-sm sm:text-base font-semibold text-foreground mb-2">
                        {ticket.title}
                      </h3>

                      <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Tag className="w-3 h-3" />
                          <span>{ticket.category?.name || "N/A"}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>
                            {new Date(ticket.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span>
                            Updated{" "}
                            {new Date(
                              ticket.updatedAt || ticket.createdAt,
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" />
                  </div>
                </Link>
              ))
            )}
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
